import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { EmailTemplateType, ColorConfig, EmailEditorState, EmailEditorContext } from './types';
import { TEMPLATE_DEFINITIONS } from './templates';
import { buildEmailHtml } from './buildEmailHtml';

export function useEmailEditor(options: {
  initialState?: Partial<EmailEditorState>;
  context?: EmailEditorContext;
}) {
  const { t } = useTranslation();
  const { initialState, context } = options;

  const getDefaults = (type: EmailTemplateType) => {
    const def = TEMPLATE_DEFINITIONS.find(td => td.type === type);
    if (!def) return null;
    return {
      subject: t(def.defaultSubjectKey, { storeName: context?.storeName || '' }),
      greeting: t(def.defaultGreetingKey, { name: context?.customerName || '{name}' }),
      ctaText: t(def.defaultCtaKey),
      bodyHtml: t(def.defaultBodyKey),
      colors: { ...def.defaultColors },
    };
  };

  const initialTemplate = initialState?.templateType || 'cart-recovery';
  const defaults = getDefaults(initialTemplate);

  const [templateType, setTemplateType] = useState<EmailTemplateType>(initialTemplate);
  const [subject, setSubject] = useState(initialState?.subject || defaults?.subject || '');
  const [greeting, setGreeting] = useState(initialState?.greeting || defaults?.greeting || '');
  const [bodyHtml, setBodyHtml] = useState(initialState?.bodyHtml || defaults?.bodyHtml || '');
  const [ctaText, setCtaText] = useState(initialState?.ctaText || defaults?.ctaText || '');
  const [ctaUrl, setCtaUrl] = useState(initialState?.ctaUrl || context?.storeUrl || '');
  const [colors, setColors] = useState<ColorConfig>(initialState?.colors || defaults?.colors || { brandColor: '#2F6FAC', buttonColor: '#2F6FAC', textColor: '#111111', backgroundColor: '#FAFAFA' });

  const selectTemplate = useCallback((type: EmailTemplateType) => {
    const defs = getDefaults(type);
    if (!defs) return;
    setTemplateType(type);
    setSubject(defs.subject);
    setGreeting(defs.greeting);
    setBodyHtml(defs.bodyHtml);
    setCtaText(defs.ctaText);
    setColors(defs.colors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, t]);

  const setColor = useCallback((key: keyof ColorConfig, value: string) => {
    setColors(prev => ({ ...prev, [key]: value }));
  }, []);

  const editorState: EmailEditorState = useMemo(() => ({
    templateType, subject, greeting, bodyHtml, ctaText, ctaUrl, colors,
  }), [templateType, subject, greeting, bodyHtml, ctaText, ctaUrl, colors]);

  const previewHtml = useMemo(
    () => buildEmailHtml(editorState, context),
    [editorState, context]
  );

  return {
    templateType, subject, greeting, bodyHtml, ctaText, ctaUrl, colors,
    selectTemplate, setSubject, setGreeting, setBodyHtml, setCtaText, setCtaUrl,
    setColor,
    editorState, previewHtml,
  };
}
